import React, { Suspense } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import "./i18n";

// ── Eagerly loaded (needed immediately on any page) ──
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import SignupContinue from "./pages/SignupContinue";
import BookingPageContainer from "./pages/BookingPage/index";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { PermissionProvider } from "./contexts/PermissionContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { TeamProvider } from "./contexts/TeamContext";
import { BillingProvider } from "./contexts/BillingContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { NetworkStatusMonitor } from "./hooks/useNetworkStatus";
import { useReferralCookie } from "./hooks/useReferralCookie";
import { AffiliateAuthProvider } from "./contexts/AffiliateAuthContext";
import AnnouncementProvider from "./components/Announcements/AnnouncementProvider";
import OrganizationRequiredModal from "./components/OrganizationRequiredModal";
import LoadingSpinner from "./components/core/LoadingSpinner/LoadingSpinner";

// ── Lazy-loaded pages (split into separate chunks, loaded on demand) ──
// Core app pages — loaded when user navigates to authenticated routes
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const MeetingTypes = React.lazy(() => import("./pages/MeetingTypes"));
const PollAnswer = React.lazy(() => import("./pages/PollAnswer"));
const PollCreate = React.lazy(() => import("./pages/PollCreate"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const AvailabilityPage = React.lazy(() => import("./pages/AvailabilityPage"));
const IntegrationsPage = React.lazy(() => import("./pages/IntegrationsPage"));
const IntegrationDetailPage = React.lazy(() => import("./pages/IntegrationDetailPage"));
const IntegrationStripePage = React.lazy(() => import("./pages/IntegrationStripePage"));
const VideoConferenceSettings = React.lazy(() => import("./pages/VideoConferenceSettings"));
const ContactsPage = React.lazy(() => import("./pages/ContactsPage"));
const ProductsPage = React.lazy(() => import("./pages/ProductsPage"));
const CustomerResetPassword = React.lazy(() => import("./pages/CustomerResetPassword"));
const ShopPage = React.lazy(() => import("./pages/ShopPage"));
const TransactionsPage = React.lazy(() => import("./pages/TransactionsPage"));
const ContactSettingsPage = React.lazy(() => import("./pages/ContactSettingsPage"));
const FormsPage = React.lazy(() => import("./pages/FormsPage"));
const CommunicationTemplatesPage = React.lazy(() => import("./pages/CommunicationTemplatesPage"));
const OrganizationAnalyticsPage = React.lazy(() => import("./pages/OrganizationAnalyticsPage"));
const OnboardingWizard = React.lazy(() => import("./pages/Onboarding/OnboardingWizard"));

// Auth/OAuth pages — infrequently visited
const OAuthCallback = React.lazy(() => import("./pages/OAuthCallback"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const CalendarCallback = React.lazy(() => import("./pages/CalendarCallback"));
const AcceptInvitation = React.lazy(() => import("./pages/AcceptInvitation"));
const OrganizationInvitation = React.lazy(() => import("./pages/OrganizationInvitation"));
const ConfirmLinkAccount = React.lazy(() => import("./pages/ConfirmLinkAccount"));

// Affiliate portal pages — separate route tree at /affiliates/*
const AffiliatesLayout = React.lazy(() => import("./pages/affiliates/AffiliatesLayout"));
const AffiliatesMarketingPage = React.lazy(() => import("./pages/affiliates/AffiliatesMarketingPage"));
const AffiliatesLogin = React.lazy(() => import("./pages/affiliates/AffiliatesLogin"));
const AffiliatesOAuthCallback = React.lazy(() => import("./pages/affiliates/AffiliatesOAuthCallback"));
const AffiliatesApply = React.lazy(() => import("./pages/affiliates/AffiliatesApply"));
const AffiliatesApplicationStatus = React.lazy(() => import("./pages/affiliates/AffiliatesApplicationStatus"));
const AffiliatesDashboard = React.lazy(() => import("./pages/affiliates/AffiliatesDashboard"));
const AffiliatesLinks = React.lazy(() => import("./pages/affiliates/AffiliatesLinks"));
const AffiliatesCommissions = React.lazy(() => import("./pages/affiliates/AffiliatesCommissions"));
const AffiliatesPayouts = React.lazy(() => import("./pages/affiliates/AffiliatesPayouts"));
const AffiliatesSettings = React.lazy(() => import("./pages/affiliates/AffiliatesSettings"));
const AffiliatesRequireAuth = React.lazy(() => import("./pages/affiliates/AffiliatesRequireAuth"));

// Admin panel — only loaded for admin users (includes heavy deps: recharts, tables)
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminOrganizations = React.lazy(() => import("./pages/admin/AdminOrganizations"));
const AdminAnalytics = React.lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminTrackingPixels = React.lazy(() => import("./pages/admin/AdminTrackingPixels"));
const AdminCancellations = React.lazy(() => import("./pages/admin/AdminCancellations"));
const AdminAuditLogs = React.lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminInquiries = React.lazy(() => import("./pages/admin/AdminInquiries"));
const AdminPlanManagement = React.lazy(() => import("./pages/admin/AdminPlanManagement"));
const AdminTransactions = React.lazy(() => import("./pages/admin/AdminTransactions"));
const AdminTaxSettings = React.lazy(() => import("./pages/admin/AdminTaxSettings"));
const AdminFeatureRollout = React.lazy(() => import("./pages/admin/AdminFeatureRollout"));
const AdminEmailSettings = React.lazy(() => import("./pages/admin/AdminEmailSettings"));
const AdminJobMonitor = React.lazy(() => import("./pages/admin/AdminJobMonitor"));
const AdminCommunication = React.lazy(() => import("./pages/admin/AdminCommunication"));
const AdminEmailTemplate = React.lazy(() => import("./pages/admin/AdminEmailTemplate"));
const AdminAffiliates = React.lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminAffiliateDetail = React.lazy(() => import("./pages/admin/AdminAffiliateDetail"));
const AdminAffiliateSettings = React.lazy(() => import("./pages/admin/AdminAffiliateSettings"));
const AdminAffiliateCampaigns = React.lazy(() => import("./pages/admin/AdminAffiliateCampaigns"));
const AdminAffiliatePayouts = React.lazy(() => import("./pages/admin/AdminAffiliatePayouts"));

// Placeholder for 404 page
const NotFound = () => <div>Page Not Found</div>;

// Simple health/debug component
const HealthComponent = () => {
  return (
    <div style={{ padding: "2rem", fontFamily: "monospace" }}>
      <h1>Client Health Check</h1>
      <p>Client application is running correctly.</p>
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem",
          background: "#f0f0f0",
          borderRadius: "4px",
        }}
      >
        <h2>Environment Variables:</h2>
        <pre>
          {JSON.stringify(
            {
              REACT_APP_API_URL: process.env.REACT_APP_API_URL || "Not set",
              NODE_ENV: process.env.NODE_ENV || "Not set",
              PUBLIC_URL: process.env.PUBLIC_URL || "Not set",
            },
            null,
            2
          )}
        </pre>
      </div>
    </div>
  );
};

/**
 * AuthenticatedProviders
 *
 * Layout route wrapper that provides Permission, Organization, Team, Billing,
 * and Announcement contexts to authenticated routes only.
 *
 * Public routes (booking page, login, signup, etc.) do NOT pass through
 * these providers, which eliminates unnecessary API calls:
 *   - /api/users/me/permissions
 *   - /api/users/me/organizations
 *   - /api/teams/my-teams
 *   - /api/billing/current
 *   - /api/announcements/popups/pending
 *
 * AuthProvider stays global (wraps all routes) because:
 *   - It's lightweight: only calls /api/users/me if a token exists in localStorage
 *   - The booking page optionally uses useAuth() to detect logged-in state
 */
const AuthenticatedProviders: React.FC = () => (
  <PermissionProvider>
    <OrganizationProvider>
      <TeamProvider>
        <BillingProvider>
          <AnnouncementProvider>
            <OrganizationRequiredModal />
            <Outlet />
          </AnnouncementProvider>
        </BillingProvider>
      </TeamProvider>
    </OrganizationProvider>
  </PermissionProvider>
);

const App: React.FC = () => {
  // Capture ?ref=CODE on first mount and forward to the affiliate tracking endpoint.
  // Server records the click and sets the saillab_ref cookie (HttpOnly, parent domain).
  useReferralCookie();
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          {/*
            Toast Notification Configuration

            Uses brand color system for consistent styling:
            - Success: Light blue background with brand color text (matches availability UI)
            - Error: Light red background with dark red text
            - Brand colors are CSS variables set by brandColorTheme.ts

            Individual toasts styled via useToast hook for i18n enforcement
          */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 5000,
              // Default styling (overridden by useToast hook)
              style: {
                background: "#FFFFFF",
                color: "#374151",
                border: "1px solid #E5E7EB",
                padding: "16px",
                fontSize: "14px",
                fontWeight: "500",
              },
              // Success toasts: Brand-themed light blue
              success: {
                style: {
                  background: "var(--brand-color-very-light, #eaf2f5)",
                  color: "var(--brand-color, #2E7AA0)",
                  border: "1px solid var(--brand-color-light, #A3D3EB)",
                  padding: "16px",
                  fontSize: "14px",
                  fontWeight: "500",
                },
                iconTheme: {
                  primary: "var(--brand-color, #2E7AA0)",
                  secondary: "var(--brand-color-very-light, #eaf2f5)",
                },
              },
              // Error toasts: Light red
              error: {
                style: {
                  background: "#FEF2F2",
                  color: "#991B1B",
                  border: "1px solid #FCA5A5",
                  padding: "16px",
                  fontSize: "14px",
                  fontWeight: "500",
                },
                iconTheme: {
                  primary: "#991B1B",
                  secondary: "#FEF2F2",
                },
                duration: 6000, // Errors shown slightly longer
              },
            }}
            containerStyle={{}}
            gutter={8}
          />
          {/* Network status monitoring - shows offline/online toasts */}
          <NetworkStatusMonitor />
          {/* Suspense boundary for lazy-loaded route components */}
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>}>
          <Routes>
            {/* ============================================
                PUBLIC ROUTES
                ============================================
                These routes only have AuthProvider + LanguageProvider.
                They do NOT load Permission, Organization, Team, or
                Announcement contexts — saving 4+ API calls per page load.
            */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/signup/continue" element={<PublicRoute><SignupContinue /></PublicRoute>} />
            {/* Email/password auth flows. reset-password/verify-email are NOT
                wrapped in PublicRoute: their emailed token links must work even
                when a session already exists (e.g. admin-initiated reset). */}
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/health" element={<HealthComponent />} />
            {/* Team invitation acceptance - must be before /:username/:slug */}
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            {/* Organization invitation acceptance (onboarding invites land here) */}
            <Route path="/organization-invitation/:token" element={<OrganizationInvitation />} />
            {/* OAuth account linking confirmation - public route for cross-provider linking */}
            <Route path="/confirm-link-account" element={<ConfirmLinkAccount />} />
            {/* Where the customer's "set your password" email lands. Declared
                BEFORE /:username/:slug so it is not parsed as a booking URL, and
                its prefix is registered in web/src/lib/bookingPath.ts. */}
            <Route path="/account/reset" element={<CustomerResetPassword />} />

            {/* The host's shop: /:username/shop/plans and /:username/shop/points.
                Declared BEFORE /:username/:slug — three segments would not match
                that route anyway, but keeping them adjacent is what stops a
                future two-segment shop URL being swallowed as a booking slug. */}
            <Route
              path="/:username/shop/:kind"
              element={
                <CustomerAuthProvider>
                  <ShopPage />
                </CustomerAuthProvider>
              }
            />

            {/* Public booking routes carry the CUSTOMER session (invitee sign-in
                for meeting types locked to points or a plan). Deliberately not
                app-wide: it is a different identity from the host's, and other
                pages have no use for it. */}
            <Route
              path="/:username/:slug"
              element={
                <CustomerAuthProvider>
                  <BookingPageContainer />
                </CustomerAuthProvider>
              }
            />
            <Route
              path="/reschedule/:token"
              element={
                <CustomerAuthProvider>
                  <BookingPageContainer mode="reschedule" />
                </CustomerAuthProvider>
              }
            />
            <Route
              path="/cancel/:token"
              element={
                <CustomerAuthProvider>
                  <BookingPageContainer mode="cancel" />
                </CustomerAuthProvider>
              }
            />
            <Route path="/calendar/callback" element={<CalendarCallback />} />

            {/* Answering a schedule poll — PUBLIC on purpose. The people
                voting are the host's guests and have no SailLab account.
                Note: this path prefix must also be listed in
                web/src/lib/bookingPath.ts APP_ROUTE_PREFIXES. */}
            {/* The free, no-signup poll creator. Declared BEFORE the answering
                route so the literal segment wins — publicIds are 22-character
                nanoids, so "new" can never be one, but relying on that rather
                than on route order would be luck. */}
            <Route path="/poll/new" element={<PollCreate />} />
            <Route path="/poll/:publicId" element={<PollAnswer />} />

            {/* ============================================
                AFFILIATE PORTAL ROUTES (subdirectory /affiliates/*)
                ============================================
                Separate auth context (affiliate JWT, aud='affiliate'). Does NOT
                load PermissionProvider / OrganizationProvider / TeamProvider —
                affiliates are partners, not SailLab users.

                Login/signup/oauth-callback render OUTSIDE AffiliatesLayout
                because they use the full-page AuthLayout (matches the main
                app's /login and /signup design — sailboat image, language
                selector at the bottom, etc.).
            */}
            <Route
              path="/affiliates/login"
              element={
                <AffiliateAuthProvider>
                  <AffiliatesLogin mode="login" />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/affiliates/signup"
              element={
                <AffiliateAuthProvider>
                  <AffiliatesLogin mode="signup" />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/affiliates/oauth/callback"
              element={
                <AffiliateAuthProvider>
                  <AffiliatesOAuthCallback />
                </AffiliateAuthProvider>
              }
            />
            {/* Marketing page is standalone (its own header/footer with logo) — NOT inside AffiliatesLayout */}
            <Route
              path="/affiliates"
              element={
                <AffiliateAuthProvider>
                  <AffiliatesMarketingPage />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/affiliates"
              element={
                <AffiliateAuthProvider>
                  <AffiliatesLayout />
                </AffiliateAuthProvider>
              }
            >
              {/* The two routes reachable while pending/rejected — they ARE the
                  approval gate, so they must not redirect to themselves. */}
              <Route
                path="apply"
                element={
                  <AffiliatesRequireAuth allowUnapproved>
                    <AffiliatesApply />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="status"
                element={
                  <AffiliatesRequireAuth allowUnapproved>
                    <AffiliatesApplicationStatus />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="dashboard"
                element={
                  <AffiliatesRequireAuth>
                    <AffiliatesDashboard />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="links"
                element={
                  <AffiliatesRequireAuth>
                    <AffiliatesLinks />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="commissions"
                element={
                  <AffiliatesRequireAuth>
                    <AffiliatesCommissions />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="payouts"
                element={
                  <AffiliatesRequireAuth>
                    <AffiliatesPayouts />
                  </AffiliatesRequireAuth>
                }
              />
              <Route
                path="settings"
                element={
                  <AffiliatesRequireAuth>
                    <AffiliatesSettings />
                  </AffiliatesRequireAuth>
                }
              />
            </Route>

            {/* ============================================
                AUTHENTICATED ROUTES
                ============================================
                Wrapped in AuthenticatedProviders layout route which adds:
                - PermissionProvider  → /api/users/me/permissions
                - OrganizationProvider → /api/users/me/organizations
                - TeamProvider         → /api/teams/my-teams
                - AnnouncementProvider → /api/announcements/popups/pending
                These only fire when user is authenticated.
            */}
            <Route element={<AuthenticatedProviders />}>
              {/* Onboarding route - authenticated but bypasses onboarding check */}
              <Route
                path="/onboarding"
                element={
                  <PrivateRoute skipOnboardingCheck={true}>
                    <OnboardingWizard />
                  </PrivateRoute>
                }
              />

              {/* Protected routes */}
              <Route
                path="/bookings"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/schedule"
                element={
                  <PrivateRoute>
                    <MeetingTypes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/availability"
                element={
                  <PrivateRoute>
                    <AvailabilityPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contacts"
                element={
                  <PrivateRoute>
                    <ContactsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <PrivateRoute>
                    <ProductsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contacts/settings"
                element={
                  <PrivateRoute>
                    <ContactSettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <PrivateRoute>
                    <TransactionsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/insights"
                element={
                  <PrivateRoute>
                    <OrganizationAnalyticsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/forms"
                element={
                  <PrivateRoute>
                    <FormsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/communication"
                element={
                  <PrivateRoute>
                    <CommunicationTemplatesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/video-conference-settings"
                element={
                  <PrivateRoute>
                    <VideoConferenceSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/integrations"
                element={
                  <PrivateRoute>
                    <IntegrationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/integration/stripe"
                element={
                  <PrivateRoute>
                    <IntegrationStripePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/integration/:integrationId"
                element={
                  <PrivateRoute>
                    <IntegrationDetailPage />
                  </PrivateRoute>
                }
              />

              {/* Admin Panel Routes - Protected by admin.full_access permission */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <AdminLayout />
                  </PrivateRoute>
                }
              >
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="organizations" element={<AdminOrganizations />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="analytics/tracking-pixels" element={<AdminTrackingPixels />} />
                <Route path="analytics/cancellations" element={<AdminCancellations />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="plan-management" element={<AdminPlanManagement />} />
                <Route path="tax-settings" element={<AdminTaxSettings />} />
                <Route path="feature-rollout" element={<AdminFeatureRollout />} />
                <Route path="email-settings" element={<AdminEmailSettings />} />
                <Route path="communication" element={<AdminCommunication />} />
                <Route path="communication/email-template" element={<AdminEmailTemplate />} />
                <Route path="monitoring/transactions" element={<AdminTransactions />} />
                <Route path="monitoring/jobs" element={<AdminJobMonitor />} />
                <Route path="affiliates" element={<AdminAffiliates />} />
                <Route path="affiliates/settings" element={<AdminAffiliateSettings />} />
                <Route path="affiliates/campaigns" element={<AdminAffiliateCampaigns />} />
                {/* Before the `:id` route — otherwise "payouts" is read as an id. */}
                <Route path="affiliates/payouts" element={<AdminAffiliatePayouts />} />
                <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
                <Route index element={<AdminDashboard />} />
              </Route>
            </Route>

            {/* Root route: redirect authenticated users to /schedule, show Login otherwise */}
            <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />

            {/* Catch all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </LanguageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
